import React, { useContext, useEffect } from 'react';
import { Environment } from 'src/Environment';
import { Route, Switch, withRouter, Redirect, useLocation, useHistory } from 'react-router-dom';
import {
  onboardingBusinessTypeReskinRoute,
  onboardingChooseSolutionReskinRoute,
  onboardingBusinessDetailsReskinYourBusinessRoute,
  onboardingBusinessDetailsReskinYourBusinessAddressRoute,
  onboardingYourDetailsReskinYourselfRoute,
  onboardingYourDetailsReskinSelfieRoute,
  onboardingYourDetailsReskinBankingRoute,
  onboardingConfirmReskinRoute,
  onboardingUploadDocumentsReskinRoute,
  onboardingLetsGetStartedRoute,
  onboardingThankYouReskinRoute,
  onboardingFailureRoute,
  onboardingBusinessDetailsReskinYourBusinessIdsRoute,
  onboardingFicaProcessingRoute,
  onboardingCreateAccountRedirect,
  onboardingBusinessDetailsDirectorsAddress,
  onboardingYourDetailsCustomersAddress,
  onboardingYourDetailsMoreDetails,
  onboardingChoosePaymentTypes,
  onboardingPendingApplication,
  onboardingLegacyDeclinedApp,
} from 'src/routing/routes';
import ChooseSolution from './choose-solution/choose-solution';
import UploadDocumentsReskin from './upload-documents/upload-documents';
import YourBusiness from './your-business/your-business';
import YourDetails from './your-details/your-details';
import ConfirmApplication from './confirm/confirm';
import {
  CustomerTypes,
  ecommerceProductCategoryName,
  SalesforceContext,
  Steps,
  paymentRequestProductCategoryName,
} from 'src/context/salesforce-context';
import Wizard from './wizard/wizard';
import BusinessType from './business-type/business-type';
import YourBusinessAddress from './your-business-address/your-business-address';
import YourDetailsSelfie from './your-details-selfie/your-details-selfie';
import YourDetailsBanking from './your-details-banking/your-details-banking';
import ThankYou from './thank-you/thank-you';
import Failure from './failure/failure';
import { OnboardingSummaryProvider } from 'src/context/onboarding-summary-context';
import YourBusinessIds from './your-business-ids/your-business-ids';
import YourDirectorAddress from 'src/containers/onboarding/create-account/your-directors-address/your-directors-address';
import MoreDetails from 'src/containers/onboarding/create-account/more-details/more-details';
import YourResidentialAddress from 'src/containers/onboarding/create-account/your-residential-address/your-residential-address';
import PaymentTypes from './payment-types/payment-types';
import TealiumEvents from '../../../helpers/tealium-events';
import FicaProcessing from './fica-processing/fica-processing';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { ImageCache } from 'src/components/image-cache/image-cache';
import {
  CameraIconBold,
  CloseIconWhite,
  CloudUpload,
  Connecting,
  DeviceSelected,
  Download,
  EditPen,
  Error as ErrorImage,
  IceCreamImage,
  InputErrorIcon,
  Print,
  RemoveIconThin,
  RocketImage,
  SelfieIcon,
  TickedCheckbox,
  TickIcon,
  TickRedWhiteCircle,
  UntickedCheckbox,
  SelectedRadio,
  UnselectedRadio,
  RemoveIcon,
  ToolTipArrow,
  ToolTipArrowMobile,
  UpArrow,
  DownArrow1,
} from 'src/assets/images';
import { FeesContext } from 'src/context/fees-context';
import useReducer from 'src/hooks/useReducer';
import { WizardModelNames } from 'src/containers/onboarding/state/wizard/wizard-model-names';
import { getValue, urlQueryParams } from 'src/helpers/custom-url-search-param';
import { isProductCategorySelected } from 'src/helpers/devices';
import { ErrorSummary } from './error-summary/error-summary';
import { AcquiringChannelFlags } from 'src/context/salesforce-context';
import './create-account.scss';

function CreateAccount() {
  const {
    save,
    lead,
    merchant,
    isLoadingFromSessionStorage,
    hasBuyOnlineDevice,
    customerType,
    setApplicationComplete,
    isVodapayChannel,
    setPriceTier,
    updateOpportunityCardFees,
    isUseFixedCardFees,
    uniqueTerminals,
    isVodacomBusinessChannel,
    getTotalDevices,
  } = useContext(SalesforceContext);
  const history = useHistory();

  const isCreditVettingEnabled =
    !Environment.current?.disabledFeatures?.creditVetting &&
    merchant?.opportunity?.solutionType === AcquiringChannelFlags.rentOnlineFicaOnline;

  const isEcommerceSelected = isProductCategorySelected(
    ecommerceProductCategoryName,
    merchant?.opportunity?.deliveries,
  );

  const isPaymentRequestSelected = isProductCategorySelected(
    paymentRequestProductCategoryName,
    merchant?.opportunity?.deliveries,
  );

  const { isAllFeesLoaded, getFilteredCardFees, getOpportunityCardFees } = useContext(FeesContext);
  const { registerModel } = useReducer();

  useEffect(() => {
    if (
      isAllFeesLoaded() &&
      merchant?.opportunity?.deliveries &&
      Object.values(uniqueTerminals).length > 0
    ) {
      const filteredCardFees = getFilteredCardFees(isUseFixedCardFees());
      const opportunityCardFees = getOpportunityCardFees(filteredCardFees);
      setPriceTier(filteredCardFees[0]?.pricingTierName);
      updateOpportunityCardFees(opportunityCardFees);
    }
  }, [merchant, uniqueTerminals]);

  useEffect(() => {
    if (merchant.isPendingApp && merchant.pageUpdates.length === 0) {
      history.push(onboardingLegacyDeclinedApp);
    }
  }, [merchant, merchant.isPendingApp, merchant.pageUpdates, history]);

  useEffect(() => {
    const model = {
      name: WizardModelNames.WIZARD,
      backEnabled: false,
      nextEnabled: false,
      pageLoaderEnabled: false,
      isReady: true,
    };

    const removeModel = registerModel(model);

    return () => removeModel();
  }, []);

  /* takes array of steps, if a step has no substeps, returns step
  as is. if step has substeps, it copies the parent step details
  into substep, and returns updated substep, as a normal step.*/
  const unpackSteps = (steps) =>
    steps.flatMap((step) => {
      if (step.subSteps) {
        return step.subSteps.map((subStep) => ({
          ...step,
          ...subStep,
          subSteps: null,
        }));
      } else {
        return [step];
      }
    });

  const getOtpStepToResumeFrom = (steps, stepName, subStepName, skipCurrentStep) => {
    let index = steps.findIndex((step) => {
      const stepNameMatches = step.stepName === stepName;
      const subStepNameMatches = !subStepName || step.subStepName === subStepName;
      return stepNameMatches && subStepNameMatches;
    });

    if (skipCurrentStep) {
      index = index + 1;
    }

    return steps[index];
  };

  const getProductsWithoutCategory = (productCategory) => {
    const productList = [];
    Object.values(merchant?.opportunity?.deliveries)
      .filter(
        (product) =>
          product.number_of_devices > 0 &&
          product.product_category === productCategory &&
          !product.is_product_category,
      )
      .forEach((product) => {
        productList.push(product.device_name);
      });
    return productList;
  };

  const hideStepIfPendingApp = (step) => {
    if (!merchant?.isPendingApp) {
      return false;
    }

    if (!merchant.pageUpdates) {
      throw new Error(
        'Create Account: attempting to apply pending app page updates when not in pending app',
      );
    }

    return !merchant.pageUpdates.map(({ subStepName }) => subStepName).includes(step);
  };

  const steps = unpackSteps([
    {
      stepName: Steps.BUSINESS_TYPE,
      label: 'Business type',
      route: onboardingBusinessTypeReskinRoute,
      component: BusinessType,
      onStepSave: (suppressValidation) => save(Steps.BUSINESS_TYPE, null, suppressValidation),
      hideNavBar: true,
      backRoute: onboardingLetsGetStartedRoute,
      disableBack: isVodacomBusinessChannel,
    },
    {
      stepName: Steps.CHOOSE_A_SOLUTION,
      label: 'Solution',
      subSteps: [
        {
          subStepName: Steps.CHOOSE_A_SOLUTION,
          label: 'Solution',
          route: onboardingChooseSolutionReskinRoute,
          component: ChooseSolution,
          onStepSave: (suppressValidation) =>
            save(Steps.CHOOSE_A_SOLUTION, null, suppressValidation),
          disableBack: true,
          nextNeedsSovIncompleteModal: true,
          hide: () => isVodapayChannel || hideStepIfPendingApp(Steps.CHOOSE_A_SOLUTION),
        },
        {
          subStepName: Steps.CHOOSE_ECOMMERCE_PRODUCT,
          route: onboardingChoosePaymentTypes,
          component: PaymentTypes,
          onStepSave: (suppressValidation) =>
            save(Steps.CHOOSE_A_SOLUTION, Steps.CHOOSE_ECOMMERCE_PRODUCT, suppressValidation),
          disableBack: false,
          nextNeedsSovIncompleteModal: false,
          hide: () => !isEcommerceSelected && !isPaymentRequestSelected,
          onStepComplete: () => {
            TealiumEvents.paymentTypeNextClick(
              customerType,
              getProductsWithoutCategory(ecommerceProductCategoryName),
            );
          },
        },
      ],
    },
    {
      stepName: Steps.PENDING_APPS,
      label: 'Pending Application',
      route: onboardingPendingApplication,
      component: ErrorSummary,
      onStepSave: () => {},
      onStepComplete: () => {
        TealiumEvents.errorSummaryEnd();
      },
      disableBack: true,
      hide: () => !merchant.isPendingApp,
    },
    {
      stepName: Steps.YOUR_BUSINESS,
      label: 'Business details',
      subSteps: [
        {
          subStepName: Steps.BUSINESS_DETAILS,
          route: onboardingBusinessDetailsReskinYourBusinessRoute,
          component: YourBusiness,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_BUSINESS, Steps.BUSINESS_DETAILS, suppressValidation),
          onStepComplete: () => {
            TealiumEvents.yourBusinessPageFormEnd(
              merchant.account.industry,
              merchant.account.nature_of_business,
              customerType,
              merchant.isPendingApp,
            );
          },
          hide: () => hideStepIfPendingApp(Steps.BUSINESS_DETAILS),
          disableBack: isVodapayChannel,
        },
        {
          subStepName: Steps.BUSINESS_ADDRESS,
          route: onboardingBusinessDetailsReskinYourBusinessAddressRoute,
          component: YourBusinessAddress,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_BUSINESS, Steps.BUSINESS_ADDRESS, suppressValidation),
          onStepComplete: () => {
            TealiumEvents.businessAddressPageFormEnd(customerType, merchant.isPendingApp);
          },
          hide: () => hideStepIfPendingApp(Steps.BUSINESS_ADDRESS),
        },
        {
          subStepName: Steps.BUSINESS_IDS,
          route: onboardingBusinessDetailsReskinYourBusinessIdsRoute,
          component: YourBusinessIds,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_BUSINESS, Steps.BUSINESS_IDS, suppressValidation),
          onStepComplete: () => {
            TealiumEvents.yourDirectorsFormEnd(
              customerType,
              merchant.isPendingApp,
              merchant.account?.directors.map((director) => director.id_type),
            );
          },
          hide: () =>
            (!isLoadingFromSessionStorage &&
              lead.typeOfBusiness === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop) ||
            hideStepIfPendingApp(Steps.BUSINESS_IDS),
        },
        {
          subStepName: Steps.DIRECTOR_ADDRESS,
          route: onboardingBusinessDetailsDirectorsAddress,
          component: YourDirectorAddress,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_BUSINESS, Steps.DIRECTOR_ADDRESS, suppressValidation),
          hide: () =>
            !(
              merchant?.account?.sov_requested &&
              lead.typeOfBusiness !== BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop
            ) || hideStepIfPendingApp(Steps.DIRECTOR_ADDRESS),
        },
      ],
    },
    {
      stepName: Steps.YOUR_DETAILS,
      label: 'Personal details',
      subSteps: [
        {
          subStepName: Steps.YOUR_DETAILS,
          route: onboardingYourDetailsReskinYourselfRoute,
          component: YourDetails,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.YOUR_DETAILS, suppressValidation),
          onStepComplete: () =>
            TealiumEvents.aboutYouFormEnd(
              merchant?.account?.business_type,
              customerType,
              merchant?.contact?.id_type,
              merchant.isPendingApp,
            ),
          hide: () => hideStepIfPendingApp(Steps.YOUR_DETAILS),
        },
        {
          subStepName: Steps.MORE_DETAILS,
          route: onboardingYourDetailsMoreDetails,
          component: MoreDetails,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.YOUR_DETAILS, suppressValidation),
          onStepComplete: () => TealiumEvents.moreDetailsFormEnd(customerType),
          hide: () => !isCreditVettingEnabled || hideStepIfPendingApp(Steps.MORE_DETAILS),
        },
        {
          subStepName: Steps.CUSTOMER_ADDRESS,
          route: onboardingYourDetailsCustomersAddress,
          component: YourResidentialAddress,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.CUSTOMER_ADDRESS, suppressValidation),
          onStepComplete: () => TealiumEvents.residentialAddressPageEnd(merchant.isPendingApp),
          hide: () =>
            !(
              isCreditVettingEnabled ||
              (merchant?.account?.sov_requested &&
                lead.typeOfBusiness === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop)
            ) || hideStepIfPendingApp(Steps.CUSTOMER_ADDRESS),
        },
        {
          subStepName: Steps.YOUR_DETAILS_SELFIE,
          route: onboardingYourDetailsReskinSelfieRoute,
          component: YourDetailsSelfie,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.YOUR_DETAILS_SELFIE, suppressValidation),
          hide: () => hideStepIfPendingApp(Steps.YOUR_DETAILS_SELFIE),
        },
        {
          subStepName: Steps.BANKING_DETAILS,
          route: onboardingYourDetailsReskinBankingRoute,
          component: YourDetailsBanking,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.BANKING_DETAILS, suppressValidation),
          hide: () =>
            (merchant?.account?.sov_requested && getTotalDevices() === 1) ||
            hideStepIfPendingApp(Steps.BANKING_DETAILS),
        },
        {
          subStepName: Steps.UPLOAD_DOCUMENTS,
          route: onboardingUploadDocumentsReskinRoute,
          component: UploadDocumentsReskin,
          onStepSave: (suppressValidation) =>
            save(Steps.YOUR_DETAILS, Steps.UPLOAD_DOCUMENTS, suppressValidation),
          hide: () => hideStepIfPendingApp(Steps.UPLOAD_DOCUMENTS),
        },
      ],
    },
    {
      stepName: Steps.CONFIRM_APPLICATION,
      label: 'Confirm',
      subSteps: [
        {
          route: onboardingConfirmReskinRoute,
          component: ConfirmApplication,
          nextButtonLabel: 'Submit application',
          nextNeedsConfirmationMessage: true,
          hideNavBar: merchant.isPendingApp,
          hideHelpFab: merchant.isPendingApp,
          hide: () => merchant.isPendingApp && customerType === CustomerTypes.agent,
          contextProvider: OnboardingSummaryProvider,
          onStepSave: (suppressValidation) => {
            if (!hasBuyOnlineDevice || merchant?.opportunity?.online_payment_order_no) {
              setApplicationComplete(true);
              return save(Steps.CONFIRM_APPLICATION, null, suppressValidation);
            } else {
              return save(
                Steps.CONFIRM_APPLICATION,
                Steps.CONFIRM_APPLICATION_PAYMENTS,
                suppressValidation,
              );
            }
          },
        },
        {
          route: onboardingThankYouReskinRoute,
          component: ThankYou,
          hideNavBar: true,
          disableBack: true,
          hide: () => customerType === CustomerTypes.agent,
          isFinalStep: true,
        },
        {
          route: onboardingFicaProcessingRoute,
          component: FicaProcessing,
          hideNavBar: true,
          disableBack: true,
          isFinalStep: true,
        },
        {
          route: onboardingFailureRoute,
          component: Failure,
          hideNavBar: true,
          disableBack: true,
          isFinalStep: true,
        },
      ],
    },
  ]);

  const location = useLocation();

  return (
    <div className="CreateAccount">
      <ImageCache
        images={[
          CameraIconBold,
          CloseIconWhite,
          CloudUpload,
          Connecting,
          DeviceSelected,
          Download,
          EditPen,
          ErrorImage,
          IceCreamImage,
          InputErrorIcon,
          Print,
          RemoveIconThin,
          RocketImage,
          SelfieIcon,
          TickedCheckbox,
          TickIcon,
          TickRedWhiteCircle,
          UntickedCheckbox,
          SelectedRadio,
          UnselectedRadio,
          RemoveIcon,
          ToolTipArrow,
          ToolTipArrowMobile,
          UpArrow,
          DownArrow1,
          RemoveIcon,
        ]}
      />
      <Switch>
        <Route
          path={onboardingCreateAccountRedirect}
          component={() => {
            const qp = urlQueryParams(location.search);
            const stepName = getValue(qp, 'step');
            const subStepName = getValue(qp, 'sub-step');
            const skipCurrentStep = getValue(qp, 'skip-current-step');
            const otpStepToResumeFrom = getOtpStepToResumeFrom(
              steps,
              stepName,
              subStepName,
              skipCurrentStep === 'true',
            );
            return <Redirect to={otpStepToResumeFrom.route} />;
          }}
        />
        <Wizard steps={steps} automationIdPrefix="Onboarding_Create_Account" />
      </Switch>
    </div>
  );
}

export default withRouter(CreateAccount);
